import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const emptyRow = (label='') => ({ label, amount:'', is_accrual:false })

export default function WeeklyPL() {
  const { user, canEditData } = useAuth()
  const [reports, setReports] = useState([])
  const [tab, setTab] = useState('new')
  const [loading, setLoading] = useState(false)
  const [autoLoading, setAutoLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [provisions, setProvisions] = useState([])
  const [showProvisions, setShowProvisions] = useState(false)
  const [newProvision, setNewProvision] = useState({ category:'', description:'', estimated_amount:'' })

  const [form, setForm] = useState({
    week_ending: ugandaDate(),
    income_rows: [emptyRow('Lettuce sales'), emptyRow('Strawberry sales'), emptyRow('Other income')],
    expense_rows: [
      emptyRow('Nutrients & chemicals'), emptyRow('Seeds & seedlings'), emptyRow('Packaging'),
      emptyRow('Electricity'), emptyRow('Fuel / delivery'), emptyRow('Equipment / repairs')
    ],
    accrual_rows: [],
    bank_balance:'', outstanding:'', notes:''
  })

  useEffect(() => { if (tab==='history') loadReports() }, [tab])
  useEffect(() => { loadProvisions() }, [])

  const loadReports = async () => {
    setLoading(true)
    const { data } = await supabase.from('weekly_pl').select('*, profiles(full_name)').order('week_ending',{ascending:false}).limit(20)
    setReports(data||[])
    setLoading(false)
  }

  const loadProvisions = async () => {
    const month = ugandaMonth()
    const { data } = await supabase.from('accrual_provisions').select('*').eq('period_month',month).order('created_at',{ascending:false})
    setProvisions(data||[])
  }

  // Auto-fill from cash logs for the selected week
  const autoFillFromCashLogs = async () => {
    setAutoLoading(true)
    const weekEnd = new Date(form.week_ending)
    const weekStart = new Date(weekEnd)
    weekStart.setDate(weekStart.getDate() - 6)

    const { data: logs } = await supabase
      .from('cash_logs')
      .select('income_rows, expense_rows, total_income, total_expenses')
      .gte('log_date', ugandaDate(weekStart))
      .lte('log_date', ugandaDate(weekEnd))

    if (!logs || logs.length === 0) {
      setError('No cash logs found for this week. Make sure daily cash logs are saved first.')
      setAutoLoading(false)
      return
    }

    // Aggregate income by product
    const incomeMap = {}
    const expenseMap = {}

    logs.forEach(log => {
      (log.income_rows||[]).forEach(r => {
        const key = r.product || r.customer || 'Other income'
        incomeMap[key] = (incomeMap[key]||0) + (parseFloat(r.total)||0)
      })
      ;(log.expense_rows||[]).forEach(r => {
        const key = r.item || 'Other expense'
        expenseMap[key] = (expenseMap[key]||0) + (parseFloat(r.amount)||0)
      })
    })

    const income_rows = Object.entries(incomeMap).map(([label,amount]) => ({ label, amount: amount.toFixed(0), is_accrual:false }))
    const expense_rows = Object.entries(expenseMap).map(([label,amount]) => ({ label, amount: amount.toFixed(0), is_accrual:false }))

    // Add unpaid accrual provisions for this period
    const accrual_rows = provisions.map(p => ({ label: p.category + (p.description?' — '+p.description:''), amount: p.estimated_amount.toFixed(0), is_accrual:true, provision_id:p.id }))

    setForm(f => ({ ...f, income_rows: income_rows.length ? income_rows : f.income_rows, expense_rows: expense_rows.length ? expense_rows : f.expense_rows, accrual_rows }))
    setAutoLoading(false)
  }

  const setIncomeRow = (i,field,val) => { const rows=[...form.income_rows]; rows[i]={...rows[i],[field]:val}; setForm(f=>({...f,income_rows:rows})) }
  const setExpenseRow = (i,field,val) => { const rows=[...form.expense_rows]; rows[i]={...rows[i],[field]:val}; setForm(f=>({...f,expense_rows:rows})) }

  const totalIncome = form.income_rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0)
  const totalCashExpenses = form.expense_rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0)
  const totalAccruals = form.accrual_rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0)
  const totalExpenses = totalCashExpenses + totalAccruals
  const net = totalIncome - totalExpenses

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false)
    const { error: err } = await supabase.from('weekly_pl').insert({
      user_id: user.id, week_ending: form.week_ending,
      income_rows: form.income_rows.filter(r=>r.amount),
      expense_rows: [...form.expense_rows.filter(r=>r.amount), ...form.accrual_rows.filter(r=>r.amount)],
      total_income: totalIncome, total_expenses: totalExpenses, net,
      bank_balance: parseFloat(form.bank_balance)||0,
      outstanding: parseFloat(form.outstanding)||0,
      notes: form.notes
    })
    if (err) { setError(err.message); setSaving(false); return }
    setSaved(true); setSaving(false)
    setTimeout(()=>setSaved(false),3000)
    setTab('history'); loadReports()
  }

  const handleAddProvision = async () => {
    if (!newProvision.category || !newProvision.estimated_amount) return
    const month = ugandaMonth()
    await supabase.from('accrual_provisions').insert({
      user_id: user.id,
      period_month: month,
      category: newProvision.category,
      description: newProvision.description,
      estimated_amount: parseFloat(newProvision.estimated_amount)
    })
    setNewProvision({ category:'', description:'', estimated_amount:'' })
    loadProvisions()
  }

  const [reconcileModal, setReconcileModal] = React.useState(null)
  const [reconcileAmount, setReconcileAmount] = React.useState('')

  const handleReconcile = (provision) => {
    setReconcileModal(provision)
    setReconcileAmount(provision.estimated_amount?.toString() || '')
  }

  const handleConfirmReconcile = async () => {
    if (!reconcileAmount) return
    await supabase.from('accrual_provisions').update({
      actual_amount: parseFloat(reconcileAmount),
      is_reconciled: true,
      reconciled_at: new Date().toISOString(),
      reconciled_by: user.id
    }).eq('id', reconcileModal.id)
    setReconcileModal(null); setReconcileAmount(''); loadProvisions()
  }

  return (
    <AppLayout title="Weekly P&L">
      <div className="section-header">
        <div className="section-icon">📊</div>
        <div className="section-info">
          <h2>Weekly P&amp;L Summary</h2>
          <p>Accrual basis — auto-fills from daily cash logs. Provisions are recorded even if not yet paid.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={()=>setTab('new')}>📝 New Report</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={()=>setTab('history')}>📚 History</button>
        <button className={`tab ${tab==='provisions'?'active':''}`} onClick={()=>setTab('provisions')}>📌 Accrual Provisions {provisions.length>0&&<span className="badge badge-amber" style={{marginLeft:6}}>{provisions.length}</span>}</button>
      </div>

      {tab==='new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Weekly P&L saved!</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18, flexWrap:'wrap' }}>
            <div className="form-group" style={{ maxWidth:220, marginBottom:0 }}>
              <label className="form-label">Week Ending</label>
              <input className="form-input" type="date" value={form.week_ending} onChange={e=>setForm(f=>({...f,week_ending:e.target.value}))} disabled={!canEditData} />
            </div>
            <button className="btn btn-secondary" style={{ marginTop:20 }} onClick={autoFillFromCashLogs} disabled={autoLoading}>
              {autoLoading ? <><span className="spinner"/>Loading...</> : '⚡ Auto-fill from Cash Logs'}
            </button>
            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:20 }}>Reads this week's daily cash logs and fills income & expenses automatically</div>
          </div>

          <div className="grid-2" style={{ gap:24 }}>
            {/* Income */}
            <div>
              <div style={{ fontWeight:700, marginBottom:10, display:'flex', justifyContent:'space-between' }}>
                <span>📈 Income This Week</span>
                <span style={{ color:'var(--green-mid)', fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem' }}>UGX {totalIncome.toLocaleString()}</span>
              </div>
              {form.income_rows.map((row,i) => (
                <div key={i} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                  <input className="form-input" style={{ flex:1 }} placeholder="Source" value={row.label} onChange={e=>setIncomeRow(i,'label',e.target.value)} disabled={!canEditData} />
                  <input className="form-input" style={{ width:130 }} type="number" placeholder="0" value={row.amount} onChange={e=>setIncomeRow(i,'amount',e.target.value)} disabled={!canEditData} />
                  {form.income_rows.length>1&&canEditData&&<button className="btn-del-row" onClick={()=>setForm(f=>({...f,income_rows:f.income_rows.filter((_,j)=>j!==i)}))}>✕</button>}
                </div>
              ))}
              {canEditData&&<button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,income_rows:[...f.income_rows,emptyRow()]}))}>+ Add Income</button>}
            </div>

            {/* Cash Expenses */}
            <div>
              <div style={{ fontWeight:700, marginBottom:10, display:'flex', justifyContent:'space-between' }}>
                <span>📉 Cash Expenses</span>
                <span style={{ color:'var(--red)', fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem' }}>UGX {totalCashExpenses.toLocaleString()}</span>
              </div>
              {form.expense_rows.map((row,i) => (
                <div key={i} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                  <input className="form-input" style={{ flex:1 }} placeholder="Expense" value={row.label} onChange={e=>setExpenseRow(i,'label',e.target.value)} disabled={!canEditData} />
                  <input className="form-input" style={{ width:130 }} type="number" placeholder="0" value={row.amount} onChange={e=>setExpenseRow(i,'amount',e.target.value)} disabled={!canEditData} />
                  {form.expense_rows.length>1&&canEditData&&<button className="btn-del-row" onClick={()=>setForm(f=>({...f,expense_rows:f.expense_rows.filter((_,j)=>j!==i)}))}>✕</button>}
                </div>
              ))}
              {canEditData&&<button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,expense_rows:[...f.expense_rows,emptyRow()]}))}>+ Add Expense</button>}
            </div>
          </div>

          {/* Accrual Provisions */}
          {form.accrual_rows.length>0 && (
            <div style={{ marginTop:20 }}>
              <div style={{ fontWeight:700, marginBottom:8, color:'var(--amber)', display:'flex', justifyContent:'space-between' }}>
                <span>📌 Accrued Expenses (not yet paid — recorded for accrual accounting)</span>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem' }}>UGX {totalAccruals.toLocaleString()}</span>
              </div>
              <div style={{ background:'var(--amber-pale)', borderRadius:8, padding:12, border:'1px solid var(--amber-light)' }}>
                {form.accrual_rows.map((row,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <span style={{ fontSize:'0.85rem' }}>📌 {row.label}</span>
                    <span style={{ fontWeight:700, fontFamily:"'DM Mono',monospace", fontSize:'0.85rem' }}>UGX {parseFloat(row.amount).toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:8 }}>These provisions are recorded as payables. Go to Accrual Provisions tab to reconcile when actual bills arrive.</div>
              </div>
            </div>
          )}

          {/* Net summary */}
          <div style={{ background:net>=0?'var(--green-pale)':'var(--red-pale)', borderRadius:10, padding:18, margin:'20px 0', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            {[['Total Income','UGX '+totalIncome.toLocaleString(),'var(--green-mid)'],['Total Expenses (incl. accruals)','UGX '+totalExpenses.toLocaleString(),'var(--red)'],['NET PROFIT / LOSS',(net>=0?'+':'')+'UGX '+net.toLocaleString(),net>=0?'var(--green-mid)':'var(--red)']].map(([label,val,color])=>(
              <div key={label} style={{ textAlign:'center' }}>
                <div style={{ fontSize:'0.65rem', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:700, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Cash in Bank (end of week)</label><input className="form-input" type="number" placeholder="0" value={form.bank_balance} onChange={e=>setForm(f=>({...f,bank_balance:e.target.value}))} disabled={!canEditData} /></div>
            <div className="form-group"><label className="form-label">Outstanding Invoices Total</label><input className="form-input" type="number" placeholder="0" value={form.outstanding} onChange={e=>setForm(f=>({...f,outstanding:e.target.value}))} disabled={!canEditData} /></div>
          </div>
          <div className="form-group"><label className="form-label">Notes / Action Items</label><textarea className="form-textarea" placeholder="e.g. Electricity cost higher than usual. Restaurant X still hasn't paid INV-003." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} disabled={!canEditData} /></div>

          {canEditData && (
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner"/>Saving...</>:'💾 Save Report'}</button>
              <button className="btn btn-ghost" onClick={()=>window.print()}>🖨️ Print</button>
            </div>
          )}
        </div>
      )}

      {tab==='history' && (
        <div className="card">
          <div className="card-title">P&L History</div>
          {loading?<div style={{textAlign:'center',padding:32}}><span className="spinner dark"/></div>
          :reports.length===0?<div className="empty-state"><div className="empty-icon">📊</div><h3>No reports yet</h3></div>
          :(
            <div className="table-wrap">
              <table>
                <thead><tr><th>Week Ending</th><th>Income (UGX)</th><th>Expenses (UGX)</th><th>Net (UGX)</th><th>Bank Balance</th><th>By</th></tr></thead>
                <tbody>
                  {reports.map(r=>(
                    <tr key={r.id}>
                      <td style={{fontWeight:600}}>{new Date(r.week_ending).toLocaleDateString('en-UG')}</td>
                      <td style={{color:'var(--green-mid)',fontWeight:600}}>{(r.total_income||0).toLocaleString()}</td>
                      <td style={{color:'var(--red)'}}>{(r.total_expenses||0).toLocaleString()}</td>
                      <td><span style={{fontWeight:700,color:r.net>=0?'var(--green-mid)':'var(--red)'}}>{r.net>=0?'+':''}{(r.net||0).toLocaleString()}</span></td>
                      <td style={{fontSize:'0.78rem'}}>{(r.bank_balance||0).toLocaleString()}</td>
                      <td style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{r.profiles?.full_name||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab==='provisions' && (
        <div>
          <div className="alert alert-amber" style={{marginBottom:16}}>
            📌 <strong>Accrual Provisions</strong> — Record expected expenses here before the bill arrives (e.g. electricity estimate). They appear in your P&L automatically as accrued expenses.<br/>
            <strong>Auto-reconcile:</strong> When you record the actual payment in Cash Log as a "Direct Purchase" or "Supplier Invoice", link it to the provision — it reconciles automatically. No double entry needed.<br/>
            <strong>Manual reconcile:</strong> If you paid outside the cash log, click "Reconcile Manually" and enter the actual amount.
          </div>

          {/* Add new provision */}
          {canEditData && (
            <div className="card" style={{marginBottom:16, border:'1px solid var(--amber)'}}>
              <div className="card-title">Add Provision</div>
              <div className="grid-3" style={{marginBottom:12}}>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={newProvision.category} onChange={e=>setNewProvision(p=>({...p,category:e.target.value}))}>
                    <option value="">Select...</option>
                    {['Electricity','Water','Wages','Rent','Insurance','Fuel','Repairs','Other'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description (optional)</label>
                  <input className="form-input" value={newProvision.description} onChange={e=>setNewProvision(p=>({...p,description:e.target.value}))} placeholder="e.g. March electricity estimate" />
                </div>
                <div className="form-group">
                  <label className="form-label">Estimated Amount (UGX)</label>
                  <input className="form-input" type="number" value={newProvision.estimated_amount} onChange={e=>setNewProvision(p=>({...p,estimated_amount:e.target.value}))} placeholder="0" />
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleAddProvision}>+ Add Provision</button>
            </div>
          )}

          <div className="card">
            <div className="card-title">Open Provisions — {new Date().toLocaleDateString('en-UG',{month:'long',year:'numeric'})}</div>
            {provisions.length===0
              ? <div className="empty-state"><div className="empty-icon">📌</div><h3>No open provisions</h3><p>Add provisions for expected expenses like electricity, wages, etc.</p></div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Category</th><th>Description</th><th>Estimated (UGX)</th><th>Actual (UGX)</th><th>Variance</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                      {provisions.map(p=>(
                        <tr key={p.id}>
                          <td style={{fontWeight:600}}>{p.category}</td>
                          <td style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>{p.description||'—'}</td>
                          <td style={{fontFamily:"'DM Mono',monospace"}}>{parseFloat(p.estimated_amount).toLocaleString()}</td>
                          <td style={{fontFamily:"'DM Mono',monospace",color:'var(--text-muted)'}}>{p.actual_amount?parseFloat(p.actual_amount).toLocaleString():'—'}</td>
                          <td>{p.actual_amount?<span style={{color:p.actual_amount>p.estimated_amount?'var(--red)':'var(--green-mid)',fontWeight:600}}>{p.actual_amount>p.estimated_amount?'+':''}{(p.actual_amount-p.estimated_amount).toLocaleString()}</span>:'—'}</td>
                          <td><span className={`badge ${p.is_reconciled?'badge-green':'badge-amber'}`}>{p.is_reconciled?'✅ Reconciled':'⏳ Open'}</span></td>
                          <td>
                            {p.is_reconciled
                              ? <span style={{fontSize:'0.72rem',color:'var(--green-mid)'}}>
                                  {p.reconciled_at ? `${new Date(p.reconciled_at).toLocaleDateString('en-UG')}` : ''}
                                  {p.actual_amount && Math.abs(p.actual_amount - p.estimated_amount) < 1 ? ' (via Cash Log)' : ''}
                                </span>
                              : canEditData && (
                                <button className="btn btn-secondary btn-sm" onClick={()=>handleReconcile(p)}>
                                  ✎ Reconcile Manually
                                </button>
                              )
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </div>
      )}

      {reconcileModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setReconcileModal(null)}>
          <div className="modal">
            <div className="modal-title">✎ Reconcile Provision</div>
            <div className="modal-subtitle">
              <strong>{reconcileModal.category}</strong>{reconcileModal.description ? ` — ${reconcileModal.description}` : ''}<br/>
              Estimated: UGX {parseFloat(reconcileModal.estimated_amount).toLocaleString()}
            </div>
            <div className="form-group">
              <label className="form-label">Actual Amount (UGX) *</label>
              <input className="form-input" type="number" value={reconcileAmount} onChange={e=>setReconcileAmount(e.target.value)} placeholder="Enter actual amount paid" autoFocus />
              {reconcileAmount && (
                <div style={{ marginTop:6, fontSize:'0.8rem', color: parseFloat(reconcileAmount) > parseFloat(reconcileModal.estimated_amount) ? 'var(--red)' : 'var(--green-mid)', fontWeight:700 }}>
                  Variance: {parseFloat(reconcileAmount) > parseFloat(reconcileModal.estimated_amount) ? '+' : ''}{(parseFloat(reconcileAmount) - parseFloat(reconcileModal.estimated_amount)).toLocaleString()} UGX
                  {parseFloat(reconcileAmount) > parseFloat(reconcileModal.estimated_amount) ? ' (over budget)' : ' (under budget)'}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setReconcileModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmReconcile} disabled={!reconcileAmount}>✓ Reconcile</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
